import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listCfbSchools } from '../cfbSchoolCatalog';
import { schoolMatchesQuery } from '../cfbSetupFormUtils';
import type { CfbSchoolCatalogEntry } from '../cfbSchoolCatalogTypes';
import CfbSchoolListRow from './CfbSchoolListRow';

export interface CfbSchoolPickerProps {
  selectedSchoolId?: string | null;
  selectedSchool?: CfbSchoolCatalogEntry | null;
  onSelect: (school: CfbSchoolCatalogEntry) => void;
  onClear?: () => void;
  disabled?: boolean;
}

const CfbSchoolPicker: React.FC<CfbSchoolPickerProps> = ({
  selectedSchoolId,
  selectedSchool,
  onSelect,
  onClear,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [catalog, setCatalog] = useState<CfbSchoolCatalogEntry[]>([]);
  const [catalogCount, setCatalogCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (disabled) {
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listCfbSchools();
        if (!cancelled) {
          setCatalog(response.schools);
          setCatalogCount(response.count || response.schools.length);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load schools.');
          setCatalog([]);
          setCatalogCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const activeSelection = useMemo(() => {
    if (selectedSchool) {
      return selectedSchool;
    }
    if (!selectedSchoolId) {
      return null;
    }
    return catalog.find((school) => school.school_id === selectedSchoolId) ?? null;
  }, [catalog, selectedSchool, selectedSchoolId]);

  const filteredSchools = useMemo(() => {
    const active = catalog.filter((school) => school.active_status === 'active');
    return active.filter((school) => schoolMatchesQuery(school, query));
  }, [catalog, query]);

  const countLabel = useMemo(() => {
    const total = catalogCount || catalog.length;
    if (!query.trim()) {
      return `${total} schools available`;
    }
    return `Showing ${filteredSchools.length} of ${total} schools`;
  }, [catalog.length, catalogCount, filteredSchools.length, query]);

  const comboboxValue = isOpen ? query : activeSelection?.athletic_name ?? query;

  return (
    <div className="space-y-2" data-testid="cfb-school-picker" ref={containerRef}>
      <label className="block">
        <span className="text-sm text-secondary-300">School</span>
        <div className="relative mt-1">
          <input
            ref={inputRef}
            className="input-field w-full"
            value={comboboxValue}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Search or select school…"
            disabled={disabled}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls="cfb-school-picker-listbox"
            aria-autocomplete="list"
            data-testid="cfb-school-search-input"
          />
        </div>
      </label>

      <p className="text-xs text-secondary-500" data-testid="cfb-school-count-copy">
        {loading ? 'Loading school catalog…' : countLabel}
      </p>

      {error ? <p className="text-sm text-red-300">{error}</p> : null}

      {isOpen && !disabled ? (
        <ul
          id="cfb-school-picker-listbox"
          className="max-h-64 overflow-y-auto rounded-md border border-secondary-800 bg-secondary-900 shadow-lg"
          data-testid="cfb-school-search-results"
          role="listbox"
        >
          {!loading && filteredSchools.length === 0 ? (
            <li className="px-3 py-2 text-sm text-secondary-400">No schools match your search.</li>
          ) : null}
          {filteredSchools.map((school) => (
            <li key={school.school_id} role="option" aria-selected={activeSelection?.school_id === school.school_id}>
              <CfbSchoolListRow
                as="button"
                school={school}
                showChevron
                selected={activeSelection?.school_id === school.school_id}
                onClick={() => {
                  onSelect(school);
                  setQuery('');
                  setIsOpen(false);
                }}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {activeSelection ? (
        <div
          className="overflow-hidden rounded-md border border-secondary-700 bg-secondary-900/40"
          data-testid="cfb-school-selected-card"
        >
          <CfbSchoolListRow school={activeSelection} />
          {onClear ? (
            <div className="border-t border-secondary-800 px-3 py-2 text-right">
              <button
                type="button"
                className="text-xs text-secondary-400 hover:text-white"
                onClick={() => {
                  onClear();
                  setQuery('');
                  inputRef.current?.focus();
                }}
              >
                Change
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CfbSchoolPicker;
